import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { axiosDelete, axiosGet, axiosPatch, axiosPost } from 'Redux/axiosHelper'
import { SendEmailPayload, SendSmsPayload } from 'types/chat'
import { ScheduleMessageResp } from 'types/contact/contact'

// get emails by attendee ids
const getEmailsByAttendeeIds = async (payload: { attendeesIds: string[] }) => {
  const response = await axiosPost(payload, `v1/contact/get-emails`)
  return response.data
}

export const useGetEmailsByAttendeeIds = () => {
  return useMutation(getEmailsByAttendeeIds)
}

const sendEmailToAttendees = async (payload: SendEmailPayload) => {
  const response = await axiosPost(payload, `v1/contact/email-attendees`)
  return response.data
}

export const useSendEmailToAttendees = () => {
  return useMutation(sendEmailToAttendees)
}

export const getAttendeesPhoneById = async (payload: {
  attendeesIds: string[]
}) => {
  const response = await axiosPost(payload, `v1/contact/get-phone-numbers`)
  return response.data
}

export const useGetAttendeesPhoneById = () => {
  return useMutation(getAttendeesPhoneById)
}

const sendSmsToAttendees = async ({
  payload,
  isScheduled,
}: {
  payload: SendSmsPayload
  isScheduled?: boolean
}) => {
  const response = await axiosPost(payload, `v1/contact/sms-attendees`)
  return response.data
}
export const useSendSmsToAttendees = () => {
  const queryClient = useQueryClient()
  return useMutation(sendSmsToAttendees, {
    onSuccess: (data, variables) => {
      if (!variables.isScheduled) return
      queryClient.setQueryData(
        ['scheduled-messages'],
        (oldData: any[] | undefined) => {
          if (!oldData) return [data]
          return [...oldData, data]
        },
      )
    },
  })
}

const updateScheduledMessage = async ({
  payload,
  smsId,
}: {
  payload: SendSmsPayload
  smsId: string
}) => {
  const response = await axiosPatch(
    payload,
    `v1/contact/scheduled-sms/${smsId}`,
  )
  return response.data
}
export const useUpdateScheduledMessage = () => {
  const queryClient = useQueryClient()
  return useMutation(updateScheduledMessage, {
    onSuccess: data => {
      queryClient.setQueryData(
        ['scheduled-messages'],
        (oldData: any[] | undefined) => {
          if (!oldData) return [data]
          return oldData.map(item => (item._id === data._id ? data : item))
        },
      )
    },
  })
}

const deleteScheduledMessage = async (smsId: string) => {
  const response = await axiosDelete(`v1/contact/remove-sms/${smsId}`)
  return response.data
}
export const useDeleteScheduledMessage = () => {
  const queryClient = useQueryClient()
  return useMutation(deleteScheduledMessage, {
    onSuccess: data => {
      queryClient.setQueryData(
        ['scheduled-messages'],
        (oldData: any[] | undefined) => {
          if (!oldData) return []
          return oldData.filter(item => item._id !== data._id)
        },
      )
    },
  })
}

const getScheduledMessages = async (schedulerId: string) => {
  const response = await axiosGet(`v1/contact/scheduled-sms/${schedulerId}`)
  return response.data
}
export const useGetScheduledMessages = (schedulerId: string) => {
  return useQuery(
    ['scheduled-messages'],
    () => (schedulerId ? getScheduledMessages(schedulerId) : null),
    {
      enabled: false,
    },
  )
}

const getScheduleMessageById = async (smsId: string) => {
  const response = await axiosGet(`v1/contact/scheduled-details/${smsId}`)
  return response.data
}
export const useGetScheduleMessageById = (smsId: string) => {
  return useQuery<ScheduleMessageResp>(
    ['scheduled-details-by-id'],
    () => (smsId ? getScheduleMessageById(smsId) : null),
    {
      enabled: false,
    },
  )
}
